import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Popconfirm,
  notification,
  Input,
  Select,
  Flex,
  DatePicker,
} from 'antd';
import type { TableProps } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from 'src/components/PageTitle/index.tsx';
import { deleteExpenseHeader, getExpensesList } from 'src/api/api.ts';
import { useAppSelector } from 'src/redux/hooks';
import dayjs from 'dayjs';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_OFFSET } from 'src/utils/constants';

/**
 * @description AllExpenses Component - Displays a paginated table of expense records with actions for editing and deleting.
 */
const AllExpenses: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const clientId = useAppSelector(
    (state) => state.client.selectedClientData.id
  );

  const [expenseList, setExpenseList] = useState<API.ExpenseHeaderCollection[]>(
    []
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expensesSearch, setExpensesSearch] = useState<API.FetchParams>({
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_PAGE_OFFSET,
  });
  const [visibleFilters, setVisibleFilters] = useState(false);
  const [searchString, setSearchString] = useState<string>('');
  const [dateRange, setDateRange] = useState<string[]>(['', '']);
  const [status, setStatus] = useState<API.ExpenseStatus | undefined>(
    undefined
  );
  const [external, setExternal] = useState<boolean | undefined>(undefined);

  /**
   * Fetches the list of expenses from the API.
   * Updates the table data and pagination details.
   */
  const fetchExpenses = async () => {
    if (!clientId || expensesSearch.client_id !== clientId) return; // Ensure clientId is set correctly
    setLoading(true);
    try {
      let payload = {
        ...expensesSearch,
        purpose: searchString,
        date_from: dateRange[0] || undefined,
        date_to: dateRange[1] || undefined,
        status: status,
        external: external,
      };
      const response = await getExpensesList(accessToken, payload);
      const expensesWithKey = response.data.items.map(
        (item: API.ExpenseHeaderCollection) => ({
          ...item,
          key: item.id, // Add a unique key based on the id
        })
      );
      setExpenseList(expensesWithKey);
      setTotalItems(response.data.total);
    } catch (error: any) {
      console.error(error);
      notification['error']({
        message: t('Error'),
        description: t('Failed to load your expenses'),
        placement: 'topRight',
        duration: 4.5,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deletes a draft expense and refreshes the expenses list.
   * @param {API.ExpenseHeaderCollection} record - The expense record to delete.
   */
  const deleteDraft = async (record: API.ExpenseHeaderCollection) => {
    setLoading(true);
    try {
      await deleteExpenseHeader(accessToken, record.id);
      fetchExpenses(); // Re-fetch the updated data
    } catch (error: any) {
      console.error('Error', error);
    }
  };

  // Combined useEffect to handle both clientId and fetching expenses
  useEffect(() => {
    if (clientId && expensesSearch.client_id !== clientId) {
      // Only update expensesSearch if client_id has changed
      setExpensesSearch((prev) => ({
        ...prev,
        client_id: clientId,
      }));
    } else if (clientId) {
      // Fetch data when clientId is available and expensesSearch is updated
      fetchExpenses();
    }
  }, [clientId, expensesSearch]);

  /**
   * Handles table pagination changes.
   * @param {number} page - The new page number.
   * @param {number} pageSize - The number of items per page.
   */
  const handleTableChange = (page: number, pageSize: number) => {
    setExpensesSearch((prev) => ({
      ...prev,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }));
    setCurrentPage(page);
  };

  const columns: TableProps<API.ExpenseHeaderCollection>['columns'] = [
    {
      title: t('Title'),
      dataIndex: 'purpose',
      key: 'purpose',
      render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text}</span>,
    },
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: t('Amount'),
      dataIndex: 'total',
      key: 'total',
      render: (number) => number.toFixed(2),
    },
    {
      title: t('Status'),
      key: 'status',
      dataIndex: 'status',
      render: (_, record) => t(record.status),
    },
    {
      title: t('Type'),
      key: 'type',
      dataIndex: 'external',
      render: (_, record) =>
        record.external ? t('Private card') : t('Company card'),
    },
    {
      title: t('Owner'),
      key: 'owner',
      dataIndex: 'external',
      render: (_, record) =>
        `${record.owner?.first_name ?? ''} ${record.owner?.last_name ?? ''}`,
    },
    {
      title: t('Actions'),
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space key={`Space-${record.id}`}>
          <Button
            key={`edit-${record.id}`}
            type="primary"
            onClick={() =>
              navigate('/new-expense', {
                state: { expenseData: record, myExpense: false },
              })
            }
          >
            {record.status === 'draft' ? t('Edit') : t('Show')}
          </Button>
          {record.status === 'draft' && (
            <Popconfirm
              key={`delete-${record.id}`}
              title={t('Sure to delete?')}
              onConfirm={() => deleteDraft(record)}
            >
              <Button type="primary" danger>
                {t('Delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card title={<PageTitle text="All Expenses" />} type="inner">
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Flex wrap gap="small">
          <Button
            type="primary"
            onClick={() =>
              navigate('/new-expense', { state: { myExpense: false } })
            }
          >
            {t('New Expense')}
          </Button>
          <Button
            onClick={() => setVisibleFilters(!visibleFilters)}
            type="text"
          >
            <FilterOutlined />
            {visibleFilters ? t('Hide filters') : t('Show filters')}
          </Button>
        </Flex>
        <Flex wrap gap="small">
          {visibleFilters && (
            <>
              <Input
                placeholder={t('Title')}
                style={{ width: 300 }}
                onChange={(event) => {
                  setSearchString(event.target.value);
                }}
                allowClear
              />
              <Select
                placeholder={t('Status')}
                value={status}
                onChange={(value) => {
                  setStatus(value ?? undefined);
                }}
                allowClear
                style={{ width: 120 }}
              >
                {['draft', 'sent', 'processed', 'failed'].map((status) => (
                  <Select.Option key={status} value={status}>
                    {t(status)}
                  </Select.Option>
                ))}
              </Select>
              <Select
                placeholder={t('Type')}
                value={external}
                onChange={(value) => {
                  setExternal(value ?? undefined);
                }}
                allowClear
                style={{ width: 100 }}
              >
                <Select.Option value={true}>{t('Private card')}</Select.Option>
                <Select.Option value={false}>{t('Company card')}</Select.Option>
              </Select>
              <RangePicker
                onChange={(_, dateStrings) => {
                  setDateRange(dateStrings);
                }}
                maxDate={dayjs()}
              />
              <Button type="primary" onClick={fetchExpenses}>
                {t('Filter Expenses')}
              </Button>
            </>
          )}
        </Flex>
        <Table
          columns={columns}
          key={'id'}
          dataSource={expenseList}
          scroll={{ x: true }}
          pagination={{
            current: currentPage,
            pageSize: expensesSearch.limit,
            total: totalItems,
            showSizeChanger: true,
            onChange: handleTableChange,
          }}
          loading={loading}
          className="aex-table"
        />
      </Space>
    </Card>
  );
};

export default AllExpenses;
